import { browserStorage, createTms, type TmsController } from '@sunflower-games/i18n-ota';
import type { i18n as I18n } from 'i18next';
import type { LDClient, LDFlagChangeset } from 'launchdarkly-react-client-sdk';

import { ldClient, resolveFlag, subscribeToServerFlags } from '../services/launchDarkly';

import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from './constants';

// Translations published by the TMS live on the CDN as <VITE_TMS_CDN_URL>/<lang>.json, one file per language,
// in the same shape as the bundled files. They are only loaded while the `tms_enabled` LaunchDarkly flag
// is on; otherwise the bundled files are used as before.
export const TMS_FLAG = 'tms_enabled';

export const getTmsConfig = () => ({
  // This app's folder for this environment, set per build mode in .env.<mode>. A build without it never fetches.
  baseUrl: import.meta.env.VITE_TMS_CDN_URL ?? '',
});

// Read only once the LD client is set: before that resolveFlag reports a missing client to NewRelic.
const readTmsFlag = (): boolean => !!ldClient && resolveFlag<boolean>(TMS_FLAG, false) === true;

let tmsI18n: I18n | null = null;
let tms: TmsController | null = null;
let detachClient: (() => void) | null = null;

// Re-reads the flag; the controller loads the current language the moment it turns on.
const syncTmsFlag = () => {
  tms?.setEnabled(readTmsFlag());
};

/** Loads every language the player switches to while `tms_enabled` is on. Called once i18next is initialised. */
export const initTms = (i18n: I18n) => {
  if (tmsI18n === i18n) return;
  tms?.dispose();
  tmsI18n = i18n;
  tms = createTms(i18n, {
    app: 'player-frontend',
    baseUrl: getTmsConfig().baseUrl,
    languages: SUPPORTED_LANGUAGES,
    defaultLanguage: DEFAULT_LANGUAGE,
    storage: browserStorage,
    // The texts still fall back; this only reports the failure.
    onError: (error, { language, stage }) =>
      window.newrelic?.noticeError(error instanceof Error ? error : new Error(String(error)), {
        context: 'tms_load',
        language,
        stage,
      }),
  });
  // Profile responses re-seed the server-evaluated flags, which resolveFlag falls back to.
  subscribeToServerFlags(syncTmsFlag);
  syncTmsFlag();
};

/**
 * Evaluates `tms_enabled` now that the LD client is set, and again whenever it changes. Returns the function that
 * stops listening to this client.
 */
export const watchTmsFlag = (client: LDClient): (() => void) => {
  detachClient?.();
  const onChange = (changes: LDFlagChangeset) => {
    if (TMS_FLAG in changes) syncTmsFlag();
  };
  client.on('ready', syncTmsFlag);
  client.on('change', onChange);
  const detach = () => {
    client.off('ready', syncTmsFlag);
    client.off('change', onChange);
    if (detachClient === detach) detachClient = null;
  };
  detachClient = detach;
  syncTmsFlag();
  return detach;
};
